import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  DateTimeInput,
  Form,
  Loading,
  NumberInput,
  ReferenceInput,
  SelectInput,
  minValue,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import type { AdminDataProvider, Market } from '../dataProvider';
import { type AccessibleRegion } from '../auth/permissions';
import type { Placement } from '../dataProvider/types';
import {
  LIFECYCLE_COLOR,
  LIFECYCLE_LABEL,
  SLOT_CHOICES,
  placementLifecycle,
} from './placementState';

/**
 * Placement edit. End-via-window, NOT soft-disable (D-placements): there is no
 * `active` flag and NO delete button. Stopping a placement is the "End placement"
 * action, which sets endsAt to NOW so the row falls to the `ended` lifecycle state
 * by the same clock derivation as everything else. `ended` is the terminal stopped
 * state; a placement is a time-boxed booking you end, not an ongoing entity you
 * disable.
 *
 * Internal merchandising at a single trust tier: every editable field (merchant,
 * slot, weight, window) is writable by an editor — there is no value-split here.
 * `market` is fixed at creation and read-only (a placement does not move bands).
 *
 * Audit for free: saving and ending both call dataProvider.update('placements');
 * the wrapper records the diff. The route is gated placements:write; region-scoped.
 */

interface PlacementEditValues {
  merchantId: number;
  slot: string;
  weight: number;
  startsAt: string;
  endsAt: string | null;
}

const toIso = (v: string): string => new Date(v).toISOString();
const sameInstant = (a: string | null, b: string | null): boolean => {
  if (a == null || b == null) return a === b;
  return Date.parse(a) === Date.parse(b);
};

const PlacementEditForm = ({
  record,
  now,
}: {
  record: Placement;
  now: number;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const lifecycle = placementLifecycle(record, now);
  const ended = lifecycle === 'ended';

  const defaultValues: PlacementEditValues = {
    merchantId: record.merchantId,
    slot: record.slot,
    weight: record.weight,
    startsAt: record.startsAt,
    endsAt: record.endsAt,
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as PlacementEditValues;

    // Build a MINIMAL patch of genuinely-changed fields, so the audit diff is
    // exactly the change (windows compared by instant, not string, to ignore the
    // datetime input's local round-trip).
    const data: Record<string, unknown> = {};
    const previousData: Record<string, unknown> = {};
    const setIf = (
      key: string,
      next: unknown,
      prev: unknown,
      equal: (a: never, b: never) => boolean = (a, b) => a === b,
    ) => {
      if (!equal(next as never, prev as never)) {
        data[key] = next;
        previousData[key] = prev;
      }
    };

    const nextEnd =
      values.endsAt == null || values.endsAt === ''
        ? null
        : toIso(values.endsAt);
    setIf('merchantId', Number(values.merchantId), record.merchantId);
    setIf('slot', values.slot, record.slot);
    setIf('weight', Number(values.weight), record.weight);
    setIf('startsAt', toIso(values.startsAt), record.startsAt, sameInstant);
    setIf('endsAt', nextEnd, record.endsAt, sameInstant);

    try {
      if (Object.keys(data).length > 0) {
        await dataProvider.update('placements', {
          id: record.id,
          data,
          previousData: { id: record.id, ...previousData },
        });
      }
      notify('Placement updated', { type: 'success' });
      redirect('list', 'placements');
    } catch {
      notify('Could not save the placement', { type: 'error' });
    }
  };

  // End placement: set the window's end to NOW. The row then derives `ended` by
  // the same clock as every other lifecycle read. No soft-disable flag is touched.
  const onEnd = async () => {
    const nowIso = new Date(now).toISOString();
    try {
      await dataProvider.update('placements', {
        id: record.id,
        data: { endsAt: nowIso },
        previousData: { id: record.id, endsAt: record.endsAt },
      });
      notify('Placement ended', { type: 'success' });
      redirect('list', 'placements');
    } catch {
      notify('Could not end the placement', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1">
          Edit placement
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={LIFECYCLE_LABEL[lifecycle]}
          color={LIFECYCLE_COLOR[lifecycle]}
        />
      </Stack>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <ReferenceInput source="merchantId" reference="merchants">
              <SelectInput
                label="Merchant"
                optionText="name"
                validate={required('Merchant is required')}
                fullWidth
              />
            </ReferenceInput>
            <SelectInput
              source="slot"
              label="Slot"
              choices={SLOT_CHOICES}
              validate={required('Slot is required')}
            />
            {/* Market is fixed at creation, read-only here (a placement does not
                move bands). */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Market (set at creation)
              </Typography>
              <Typography variant="body1">
                {record.market.toUpperCase()}
              </Typography>
            </Box>
            <NumberInput
              source="weight"
              label="Weight (share of voice)"
              validate={[required('Weight is required'), minValue(0)]}
            />
            <DateTimeInput source="startsAt" label="Starts at" />
            <DateTimeInput
              source="endsAt"
              label="Ends at (blank = open-ended)"
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          {/* End-via-window: the stopped state. No delete, no active toggle. */}
          <Button
            type="button"
            color="warning"
            variant="outlined"
            disabled={ended}
            onClick={onEnd}
          >
            End placement
          </Button>
          <Button type="button" onClick={() => redirect('list', 'placements')}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper; `asOf` injects
 *  the clock the lifecycle and the End action derive NOW from. */
export const PlacementEditView = ({ asOf }: { asOf?: number }) => {
  const { id } = useParams();
  const { accessibleRegions } = useRegion();
  const [now] = useState<number>(() => asOf ?? Date.now());
  const { data: record, isLoading } = useGetOne<Placement>('placements', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;

  // Region guard — cannot edit a placement outside the admin's market.
  const inScope = accessibleRegions.includes(
    (record.market as Market).toUpperCase() as AccessibleRegion,
  );
  if (!inScope) return <NotAuthorized />;

  return <PlacementEditForm record={record} now={now} />;
};

/** Gated entry point registered as the `placements` Resource's edit. */
export const PlacementEdit = () => (
  <RequireAccess capability="placements:write">
    <PlacementEditView />
  </RequireAccess>
);
